import { PillarPage } from 'src/constants/searchPages'

const ORIGIN = 'https://www.spotme.mx'

/**
 * Social-card dimensions. Every `og:image` on the site MUST be exactly this —
 * `summary_large_image` and the Meta/LinkedIn link card both render 1.91:1, so
 * anything else gets centre-cropped by the platform. The on-demand pages used
 * to declare their native 1448x1086 (4:3) and lost ~27% of the frame.
 */
export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630

/**
 * The generic card — moving boxes in a blue room. Correct for self-storage and
 * neutral enough for legal / company pages. It is NOT correct for parking,
 * commercial or industrial surfaces; those have their own cards below.
 */
export const DEFAULT_OG_IMAGE = `${ORIGIN}/images/cover.png`

/**
 * Per-pillar social cards, generated by `yarn og-cards`
 * (`scripts/generate-og-cards.mjs`) from the same photo each pillar's own
 * gallery renders.
 *
 * `SelfStorage` deliberately keeps `cover.png`: it is the one pillar the
 * generic card actually depicts, and the storage photo in the gallery
 * (`rentspace-storage-v3`) reads as an industrial roller door, which is a
 * weaker category signal than the boxes.
 */
const PILLAR_OG_IMAGE: Record<PillarPage, string> = {
  [PillarPage.SelfStorage]: DEFAULT_OG_IMAGE,
  [PillarPage.Parking]: `${ORIGIN}/images/og/og-parking.jpg`,
  [PillarPage.Warehouse]: `${ORIGIN}/images/og/og-comercial.jpg`,
  [PillarPage.Industrial]: `${ORIGIN}/images/og/og-industrial.jpg`
}

/** Social card for `/soluciones-logisticas` (3PL matching vertical). */
export const LOGISTICS_OG_IMAGE = `${ORIGIN}/images/og/og-logistica.jpg`

/**
 * Host-side (supply) social cards for the `/anfitrion/*` tree.
 *
 * These are separate from the pillar cards on purpose: pick the card by the
 * READER, not by the space type. A renter card answers "where do I put my
 * stuff?"; a landlord asks "what do I get out of my empty space?", so the
 * host cards show unoccupied space and carry each page's own HERO line.
 *
 * `HOST_OG_IMAGE` is the default for the host tree — the sub-pages that have
 * their own funnel (bodegas / estacionamiento / logística) override it; broker,
 * tradicional, negocio, invita and valua-tu-propiedad all sit on the default.
 */
export const HOST_OG_IMAGE = `${ORIGIN}/images/og/og-host.jpg`
export const HOST_BODEGAS_OG_IMAGE = `${ORIGIN}/images/og/og-host-bodegas.jpg`
export const HOST_PARKING_OG_IMAGE = `${ORIGIN}/images/og/og-host-parking.jpg`
export const HOST_LOGISTICS_OG_IMAGE = `${ORIGIN}/images/og/og-host-logistica.jpg`

export const getPillarOgImage = (pillar: PillarPage): string =>
  PILLAR_OG_IMAGE[pillar] ?? DEFAULT_OG_IMAGE
